'use client';

import { IconChevronRightFilled } from '@zillow/constellation-icons/react/filled/icon-chevron-right-filled';
import { type MouseEventHandler, forwardRef, useCallback, useContext } from 'react';

import { cx } from '@/styled-system/css';

import { Icon } from '../icon';
import { IconButton } from '../icon-button';
import { PaginationRootContext } from './context/pagination-root.context';
import type { PaginationNextButtonPropsInterface } from './types/pagination-next-button.types';

/**
 * The next page jump `button` element
 * @since 10.0.0
 */
export const PaginationNextButton = forwardRef<
  HTMLButtonElement,
  PaginationNextButtonPropsInterface
>((props: PaginationNextButtonPropsInterface, forwardedRef) => {
  const {
    children = (
      <Icon>
        <IconChevronRightFilled />
      </Icon>
    ),
    onClick,
    ...rest
  } = props;
  const { setPageIndex, currentPageIndex = 0, totalPages } = useContext(PaginationRootContext);

  const handleClick: MouseEventHandler<HTMLButtonElement> = useCallback(
    (event) => {
      setPageIndex?.(Math.min(currentPageIndex + 1, totalPages - 1));
      onClick?.(event);
    },
    [setPageIndex, currentPageIndex, totalPages, onClick],
  );

  return (
    <IconButton
      data-c11n-component="Pagination.NextButton"
      title="Next page"
      tone="neutral"
      shape="circle"
      emphasis="bare"
      size="sm"
      onClick={handleClick}
      disabled={currentPageIndex >= totalPages - 1}
      ref={forwardedRef}
      rel="next"
      {...rest}
      className={cx('c11n_pagination__next-button', rest.className)}
    >
      {children}
    </IconButton>
  );
});

PaginationNextButton.displayName = 'Pagination.NextButton';
